// One source of truth for the job status vocabulary.
//
// The backend owns the list (GET /api/workorder-statuses) so the office view,
// the field app and the scheduler cannot drift apart. It is fetched once and
// cached for the session; FALLBACK keeps the UI usable if that call fails and
// must stay in step with JOB_STATUSES in server.py.
import { useEffect, useState } from 'react';
import { workorderStatuses } from './api';

export const FALLBACK = [
  { value: 'Work Pool', group: 'unassigned', color: '#64748b', closed: false },
  { value: 'Pending Confirmation', group: 'open', color: '#B7791F', closed: false },
  { value: 'Scheduled', group: 'open', color: '#8BC53F', closed: false },
  { value: 'Confirmed', group: 'open', color: '#3F7D3A', closed: false },
  { value: 'En Route', group: 'active', color: '#F59E0B', closed: false },
  { value: 'In Progress', group: 'active', color: '#3b82f6', closed: false },
  { value: 'Completed', group: 'closed', color: '#10b981', closed: true },
  { value: 'Missed', group: 'exception', color: '#B23A16', closed: false },
  { value: 'Reschedule', group: 'exception', color: '#96700E', closed: false },
  { value: 'Cancelled', group: 'closed', color: '#94a3b8', closed: true },
  { value: 'Terminate Service', group: 'closed', color: '#7C887F', closed: true },
];

let cache = null;
let inFlight = null;

export function loadJobStatuses() {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = workorderStatuses
      .list()
      .then((rows) => {
        cache = Array.isArray(rows) && rows.length ? rows : FALLBACK;
        return cache;
      })
      .catch(() => {
        cache = FALLBACK;
        return cache;
      })
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Synchronous colour lookup for canvas/marker code that cannot await. */
export function statusColor(value) {
  const list = cache || FALLBACK;
  const hit = list.find((s) => s.value === value);
  return hit ? hit.color : '#8BC53F';
}

export function isClosed(value) {
  const list = cache || FALLBACK;
  const hit = list.find((s) => s.value === value);
  return hit ? !!hit.closed : false;
}

/** Tailwind badge classes, derived from the group so a new status still styles. */
const GROUP_BADGE = {
  unassigned: 'bg-slate-100 text-slate-700',
  open: 'bg-lime-100 text-lime-800',
  active: 'bg-blue-100 text-blue-700',
  exception: 'bg-red-100 text-red-700',
  closed: 'bg-emerald-100 text-emerald-700',
};

export function statusBadge(value) {
  const list = cache || FALLBACK;
  const hit = list.find((s) => s.value === value);
  return (hit && GROUP_BADGE[hit.group]) || 'bg-slate-100 text-slate-700';
}

export function useJobStatuses() {
  const [statuses, setStatuses] = useState(cache || FALLBACK);
  useEffect(() => {
    let alive = true;
    loadJobStatuses().then((rows) => { if (alive) setStatuses(rows); });
    return () => { alive = false; };
  }, []);
  return statuses;
}
